import { Injectable } from '@angular/core';
import { Observable, of, forkJoin } from 'rxjs';
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';
import { LocalStorageService } from './local.storage.service';
import { TravelBuddyChatService, SaveChatRequest } from './travelbuddy.chat.service';
import { localStorageKeys } from 'src/app/common/constants/local.storage.keys';

/**
 * Represents a guest chat entry stored in localStorage.
 * Mirrors the recentChats array structure used by ImMessageBoxComponent.
 */
export interface GuestChatEntry {
  id: string;
  title: string;
  messages: any[];
  timestamp: Date | string;
  pinned?: boolean;
  pinnedAt?: string;
  metadata?: any;
  transferred?: boolean;
}

/**
 * Result of a guest-to-user chat transfer operation.
 */
export interface TransferResult {
  success: boolean;
  transferredCount: number;
  errors?: string[];
}

/**
 * Service responsible for managing guest chat persistence in localStorage
 * and transferring guest chats to a user's backend account upon login.
 *
 * This service encapsulates all guest chat storage logic to keep it
 * separate from the main chat component and follow single responsibility.
 */
@Injectable({
  providedIn: 'root',
})
export class GuestChatStorageService {
  private inFlightTransfer$: Observable<TransferResult> | null = null;

  constructor(
    private localStorage: LocalStorageService,
    private chatService: TravelBuddyChatService
  ) {}

  /**
   * Check if there are any guest chats stored in localStorage.
   */
  hasGuestChats(): boolean {
    try {
      const stored = this.localStorage.getItem<GuestChatEntry[]>(localStorageKeys.guestRecentChats);
      return Array.isArray(stored) && stored.length > 0;
    } catch {
      return false;
    }
  }

  hasUntransferredGuestChats(): boolean {
    return this.loadGuestChats().some((chat) => !chat.transferred);
  }

  /**
   * Load guest chats from localStorage.
   * Returns an empty array if no chats exist or data is corrupted.
   */
  loadGuestChats(): GuestChatEntry[] {
    try {
      const stored = this.localStorage.getItem<GuestChatEntry[]>(localStorageKeys.guestRecentChats);
      if (stored && Array.isArray(stored)) {
        return stored.map((chat) => ({
          ...chat,
          timestamp: new Date(chat.timestamp),
        }));
      }
    } catch (error) {
      console.error('GuestChatStorageService: Failed to load guest chats:', error);
    }
    return [];
  }

  saveGuestChats(chats: GuestChatEntry[]): void {
    this.localStorage.setItem(localStorageKeys.guestRecentChats, chats || []);
  }

  /**
   * Clear all guest chats from localStorage.
   */
  clearGuestChats(): void {
    this.localStorage.removeItem(localStorageKeys.guestRecentChats);
  }

  /**
   *
   * @param hubName - The hub identifier
   * @param userId - The logged-in user's ID
   * @returns Observable that emits the transfer result
   */
  transferToUserAccount(hubName: string, userId: string): Observable<TransferResult> {
    if (this.inFlightTransfer$) {
      return this.inFlightTransfer$;
    }

    const guestChats = this.loadGuestChats().filter((chat) => !chat.transferred);

    if (!guestChats.length) {
      return of({ success: true, transferredCount: 0 });
    }

    const chatRequests = this.buildTransferRequests(guestChats);

    if (!chatRequests.length) {
      return of({ success: true, transferredCount: 0 });
    }

    const transferredIds = guestChats.map((chat) => chat.id);

    this.inFlightTransfer$ = this.chatService.bulkTransferGuestChats(hubName, userId, chatRequests).pipe(
      tap((result: any) => {
        if (result?.success !== false) {
          this.markChatsTransferred(transferredIds);
        }
      }),
      map((result: any) => ({
        success: result?.success !== false,
        transferredCount: result?.transferredCount || 0,
        errors: result?.errors,
      })),
      catchError((bulkError) => {
        console.warn(
          'GuestChatStorageService: Bulk transfer failed, falling back to individual saves:',
          bulkError
        );
        return this.transferIndividually(hubName, userId, chatRequests, transferredIds);
      }),
      finalize(() => {
        this.inFlightTransfer$ = null;
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    return this.inFlightTransfer$;
  }

  private markChatsTransferred(chatIds: string[]): void {
    if (!chatIds.length) {
      return;
    }

    const idSet = new Set(chatIds);
    const updated = this.loadGuestChats().map((chat) =>
      idSet.has(chat.id) ? { ...chat, transferred: true } : chat
    );
    this.localStorage.setItem(localStorageKeys.guestRecentChats, updated);
  }

  private buildTransferRequests(guestChats: GuestChatEntry[]): SaveChatRequest[] {
    return [...guestChats].reverse().reduce<SaveChatRequest[]>((requests, chat) => {
      const title = (chat.title || '').trim();
      if (!title) {
        return requests;
      }

      const processedMessages: any[] = [];
      let lastUserMessage: any = null;

      for (const msg of chat.messages || []) {
        if (msg.type === 'USER') {
          lastUserMessage = msg;
        } else if (msg.type === 'AI' && String(msg.msg || '').trim()) {
          processedMessages.push({
            ...msg,
            userQuery: msg.userQuery || lastUserMessage?.msg || '',
          });
          lastUserMessage = null;
        }
      }

      if (!processedMessages.length) {
        for (const msg of chat.messages || []) {
          if (msg.type === 'USER' && String(msg.msg || '').trim()) {
            processedMessages.push(msg);
          }
        }
      }

      if (!processedMessages.length) {
        return requests;
      }

      const messages = this.chatService
        .convertMessages(processedMessages)
        .filter((message) => String(message?.msg || '').trim());

      if (!messages.length) {
        return requests;
      }

      requests.push({
        title,
        messages,
        isSaved: false,
        metadata: {
          lastMessageAt: chat.metadata?.lastMessageAt || new Date(chat.timestamp).toISOString(),
        },
      });
      return requests;
    }, []);
  }

  /**
   * Fallback: transfer chats one by one using existing saveUserChat endpoint.
   * Continues on individual failures and reports results.
   */
  private transferIndividually(
    hubName: string,
    userId: string,
    chatRequests: SaveChatRequest[],
    transferredIds: string[]
  ): Observable<TransferResult> {
    if (!chatRequests.length) {
      return of({ success: true, transferredCount: 0 });
    }

    const saveObservables = chatRequests.map((chatData) =>
      this.chatService.saveUserChat(hubName, userId, chatData).pipe(
        map(() => ({ success: true as const })),
        catchError((error) => {
          console.error('GuestChatStorageService: Failed to transfer individual chat:', error);
          return of({ success: false as const, error: chatData.title });
        })
      )
    );

    return forkJoin(saveObservables).pipe(
      map((results) => {
        const transferred = results.filter((r) => r.success).length;
        const errors = results
          .filter((r): r is { success: false; error: string } => !r.success)
          .map((r) => r.error);

        return {
          success: errors.length === 0,
          transferredCount: transferred,
          errors: errors.length > 0 ? errors : undefined,
        };
      }),
      tap((result) => {
        if (result.success) {
          this.markChatsTransferred(transferredIds);
        }
      })
    );
  }
}
